import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "previewImage",
    "current",
    "removeCheckbox",
    "progress",
    "progressLabel",
    "progressContainer",
    "chooseButton",
    "clearButton",
    "fileName",
    "modal",
    "modalImage",
    "uploadOverlay",
    "uploadOverlayLabel",
    "uploadOverlayProgress"
  ]

  connect() {
    this.previewUrl = null
    this.originalFile = null
    this.cropper = null
  }

  disconnect() {
    this.destroyCropper()
    this.hideModal()
  }

  triggerFile(event) {
    event.preventDefault()
    if (this.hasInputTarget) this.inputTarget.click()
  }

  update() {
    const file = this.hasInputTarget ? this.inputTarget.files[0] : null

    if (!file) {
      this.resetSelection()
      return
    }

    if (this.hasRemoveCheckboxTarget) this.removeCheckboxTarget.checked = false
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.remove("hidden")
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = `${file.name} · PNG, JPG, or WebP up to 5 MB.`

    this.originalFile = file

    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
    this.previewUrl = URL.createObjectURL(file)

    this.modalImageTarget.src = this.previewUrl
    this.showModal()
  }

  applyCrop(event) {
    event.preventDefault()
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({
      width: 640,
      height: 640,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })

    canvas.toBlob((blob) => {
      if (!blob) return

      const filename = this.originalFile?.name || `avatar-${Date.now()}.png`
      const file = new File([ blob ], filename, { type: "image/png" })

      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.inputTarget.files = dataTransfer.files
      this.originalFile = file

      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = URL.createObjectURL(file)

      if (this.hasPreviewImageTarget) this.previewImageTarget.src = this.previewUrl
      if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden")
      if (this.hasCurrentTarget) this.currentTarget.classList.add("hidden")
      if (this.hasClearButtonTarget) this.clearButtonTarget.classList.remove("hidden")
      if (this.hasFileNameTarget) this.fileNameTarget.textContent = `${file.name} · PNG, JPG, or WebP up to 5 MB.`

      this.hideModal()
    }, "image/png")
  }

  cancelCrop(event) {
    event.preventDefault()
    this.hideModal()
    if (this.inputTarget) this.inputTarget.value = ""
    this.resetPreviewState()
  }

  clear(event) {
    event.preventDefault()
    this.clearSelection()
  }

  clearSelection({ preserveRemoveCheckbox = false } = {}) {
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.resetSelection({ preserveRemoveCheckbox })
  }

  toggleRemove(event) {
    if (event.target.checked) {
      this.clearSelection({ preserveRemoveCheckbox: true })
      if (this.hasCurrentTarget) this.currentTarget.classList.remove("hidden")
    }
  }

  uploadInitialize() {
    this.showProgress(0)
  }

  uploadStart() {
    this.showProgress(0)
  }

  uploadProgress(event) {
    this.showProgress(event.detail.progress)
  }

  uploadError() {
    this.hideProgress()
  }

  uploadEnd() {
    this.hideProgress()
  }

  showProgress(progress) {
    if (!this.hasProgressTarget || !this.hasProgressContainerTarget) return

    const numericProgress = Number(progress) || 0
    const percent = Math.max(0, Math.min(100, Math.round(numericProgress > 1 ? numericProgress : numericProgress * 100)))

    this.progressContainerTarget.classList.remove("hidden")
    this.progressTarget.value = percent
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `${percent}%`
    }
    this.showUploadOverlay(percent)
  }

  hideProgress() {
    if (!this.hasProgressTarget || !this.hasProgressContainerTarget) return
    this.progressContainerTarget.classList.add("hidden")
    this.progressTarget.value = 0
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = ""
    this.hideUploadOverlay()
  }

  showModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")

    this.destroyCropper()
    if (!this.modalImageTarget.complete) {
      this.modalImageTarget.addEventListener("load", () => this.initializeCropper(), { once: true })
    } else {
      this.initializeCropper()
    }
  }

  hideModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.destroyCropper()
  }

  initializeCropper() {
    this.destroyCropper()
    this.cropper = new Cropper(this.modalImageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      autoCropArea: 0.9,
      background: false
    })
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  resetSelection({ preserveRemoveCheckbox = false } = {}) {
    this.resetPreviewState()
    if (!preserveRemoveCheckbox && this.hasRemoveCheckboxTarget) {
      this.removeCheckboxTarget.checked = false
    }
    this.hideProgress()
    this.hideModal()
  }

  resetPreviewState() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = null
    }

    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = ""
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }

    if (this.hasCurrentTarget) this.currentTarget.classList.remove("hidden")
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.add("hidden")
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = "PNG, JPG, or WebP up to 5 MB."
    this.originalFile = null
    this.hideUploadOverlay()
  }

  showUploadOverlay(progress) {
    if (!this.hasUploadOverlayTarget || !this.hasUploadOverlayLabelTarget) return
    this.uploadOverlayTarget.classList.remove("hidden")
    this.uploadOverlayLabelTarget.textContent = `${Math.round(progress)}%`
    if (this.hasUploadOverlayProgressTarget) {
      const boundedProgress = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)))
      this.uploadOverlayProgressTarget.style.width = `${boundedProgress}%`
    }
  }

  hideUploadOverlay() {
    if (!this.hasUploadOverlayTarget || !this.hasUploadOverlayLabelTarget) return
    this.uploadOverlayTarget.classList.add("hidden")
    this.uploadOverlayLabelTarget.textContent = ""
    if (this.hasUploadOverlayProgressTarget) {
      this.uploadOverlayProgressTarget.style.width = "0%"
    }
  }
}
